// ============================================================================
// Domain constants & enums — the portable core (spec §16, §8.1).
// Framework-agnostic: no DOM, no network. Reused unchanged by the mobile app.
// These mirror the Postgres enums in the `hanja` schema so client and DB agree.
// ============================================================================

/** Account roles (hanja.app_role). */
export const ROLE = Object.freeze({
  STUDENT: 'student',
  TUTOR: 'tutor',
  ADMIN: 'admin',
});

/** Account lifecycle (hanja.account_status). */
export const ACCOUNT_STATUS = Object.freeze({
  PENDING_ADMIN: 'pending_admin',
  ACTIVE: 'active',
  DEACTIVATED: 'deactivated',
});

/** Membership tiers (hanja.membership_type). */
export const MEMBERSHIP = Object.freeze({
  FREE: 'free',
  SUBSCRIPTION: 'subscription',
  LIFETIME: 'lifetime',
});

/** What a progress/content row is about (hanja.item_type). */
export const ITEM_TYPE = Object.freeze({
  CHARACTER: 'character',
  WORD: 'word',
});

/** The two independently-tracked skill directions (hanja.skill_direction). */
export const SKILL = Object.freeze({
  RECOGNITION: 'recognition',
  PRODUCTION: 'production',
});

/** SRS mastery states (hanja.mastery_state, spec §8.1). */
export const MASTERY = Object.freeze({
  NEW: 'new',
  LEARNING: 'learning',
  FAMILIAR: 'familiar',
  MASTERED: 'mastered',
  LAPSED: 'lapsed',
});

/** Practice games / drills (hanja.game_type, spec §7.1, §7.5, §10). */
export const GAME = Object.freeze({
  MULTIPLE_CHOICE: 'multiple_choice',
  FLASHCARDS: 'flashcards',
  FILL_BLANK: 'fill_blank',
  SENTENCE_CONSTRUCTION: 'sentence_construction',
  STROKE_WRITING: 'stroke_writing',
  LISTENING: 'listening',
  TYPE_READING: 'type_reading',
  WORD_INFERENCE: 'word_inference',
  DISCRIMINATION: 'discrimination',
  HANDWRITING: 'handwriting',
  READING_CLOZE: 'reading_cloze',
});

/** Content approval lifecycle (hanja.content_status, spec §12.1). */
export const CONTENT_STATUS = Object.freeze({
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
});

/** Theme preference (hanja.theme_preference, spec §14.3). */
export const THEME = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
});

/**
 * Self-grade buttons on the flashcard surface (spec §8.1).
 * These map onto the SM-2 scheduler and onto a numeric 0–5 grade for review_log.
 */
export const GRADE = Object.freeze({
  AGAIN: 'again',
  HARD: 'hard',
  GOOD: 'good',
  EASY: 'easy',
});

/** Numeric grade written to hanja.review_log.grade (0–5). */
export const GRADE_VALUE = Object.freeze({
  [GRADE.AGAIN]: 1,
  [GRADE.HARD]: 3,
  [GRADE.GOOD]: 4,
  [GRADE.EASY]: 5,
});

/** Study session modes (hanja.study_sessions.mode). */
export const SESSION_MODE = Object.freeze({
  GUIDED: 'guided',
  FREE: 'free',
  REVIEW: 'review',
  MOCK_TEST: 'mock_test',
});
